import { AutoComplete, Col, Row } from "antd";
import { get, keyBy } from "lodash";
import PropTypes from "prop-types";
import { BiTrash } from "react-icons/bi";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { COLORS } from "shared/style/colors";
import {
  applyConditionOperation,
  CONDITION_OPERATIONS,
  operationByValue,
} from "./catalog";

const patchRightOperand = (condition, value) => ({
  ...condition,
  rightOperand: { ...condition.rightOperand, value },
});

const ConditionValueEditor = ({ condition, labels, variable, onUpdate }) => {
  const { valueType } = operationByValue(condition.operation) || {};
  if (valueType === "none") return null;
  const value = get(condition, "rightOperand.value");
  const shared = { label: labels.VALUE, placeholder: labels.VALUE };

  if (valueType === "boolean") {
    return (
      <Select
        {...shared}
        options={[
          { value: true, label: labels.YES },
          { value: false, label: labels.NO },
        ]}
        value={value}
        onChange={(next) =>
          onUpdate((current) => patchRightOperand(current, next))
        }
      />
    );
  }

  if (variable && variable.options && variable.options.length) {
    return (
      <Select
        {...shared}
        showSearch
        options={variable.options}
        value={value ?? undefined}
        onChange={(next) =>
          onUpdate((current) => patchRightOperand(current, next))
        }
      />
    );
  }

  if (valueType === "number") {
    return (
      <Input
        {...shared}
        type="number"
        value={value ?? ""}
        onChange={(event) =>
          onUpdate((current) =>
            patchRightOperand(
              current,
              event.target.value === "" ? null : Number(event.target.value),
            ),
          )
        }
      />
    );
  }

  return (
    <Input
      {...shared}
      value={value ?? ""}
      onChange={(event) =>
        onUpdate((current) => patchRightOperand(current, event.target.value))
      }
    />
  );
};

ConditionValueEditor.propTypes = {
  condition: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  variable: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
};

ConditionValueEditor.defaultProps = {
  variable: null,
};

/** Single condition row shared by the edge and field condition editors. */
const ConditionRow = ({
  condition,
  index,
  variables,
  labels,
  onUpdate,
  onRemove,
}) => {
  const variableByAccessor = keyBy(variables, "accessor");
  const variableOptions = variables.map((variable) => ({
    value: variable.accessor,
    label: variable.label,
  }));
  const operationOptions = CONDITION_OPERATIONS.map((operation) => ({
    value: operation.value,
    label: get(labels, ["OPERATIONS", operation.labelKey]) || operation.value,
  }));

  return (
    <div className="canvas-editor__panel">
      <div className="canvas-editor__row-between mb-8">
        <span className="canvas-editor__label-strong">
          {labels.CONDITION} {index + 1}
        </span>
        <div className="delete-icon" onClick={onRemove}>
          <BiTrash color={COLORS.C_DANGER} size={20} />
        </div>
      </div>
      <div className="canvas-editor__label">{labels.ACCESSOR}</div>
      <AutoComplete
        className="canvas-editor__full-width mb-12"
        options={variableOptions}
        value={get(condition, "leftOperand.accessor", "")}
        placeholder={labels.ACCESSOR}
        onChange={(next) =>
          onUpdate((current) => ({
            ...current,
            leftOperand: { ...current.leftOperand, accessor: next },
          }))
        }
        filterOption={(input, option) =>
          `${option.label} ${option.value}`
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Select
            showSearch
            label={labels.OPERATION}
            placeholder={labels.OPERATION}
            options={operationOptions}
            value={condition.operation || undefined}
            onChange={(next) =>
              onUpdate((current) => applyConditionOperation(current, next))
            }
          />
        </Col>
        <Col span={12}>
          <ConditionValueEditor
            condition={condition}
            labels={labels}
            variable={
              variableByAccessor[get(condition, "leftOperand.accessor")]
            }
            onUpdate={onUpdate}
          />
        </Col>
      </Row>
    </div>
  );
};

ConditionRow.propTypes = {
  condition: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  variables: PropTypes.array,
  labels: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

ConditionRow.defaultProps = {
  variables: [],
  labels: {},
};

export default ConditionRow;
